import React from "react";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { FieldModel } from "./types";
import TextIcon from "@material-ui/icons/TextFields";
import DateRangeIcon from "@material-ui/icons/DateRange";
import DialpadIcon from "@material-ui/icons/Dialpad";
import DehazeIcon from "@material-ui/icons/Dehaze";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import EditIcon from "@material-ui/icons/Edit";
import { typeOfDocumentsLabels } from "../../../../utils/forms.constants";

// Helper to apply alpha to hex or rgb colors
function withAlpha(color: string | undefined, alpha: number): string {
	if (!color) return "transparent";
	try {
		if (color.startsWith('#')) {
			// Handle #RGB, #RRGGBB
			let r = 0, g = 0, b = 0;
			if (color.length === 4) {
				r = parseInt(color[1] + color[1], 16);
				g = parseInt(color[2] + color[2], 16);
				b = parseInt(color[3] + color[3], 16);
			} else if (color.length === 7) {
				r = parseInt(color.slice(1, 3), 16);
				g = parseInt(color.slice(3, 5), 16);
				b = parseInt(color.slice(5, 7), 16);
			}
			return `rgba(${r}, ${g}, ${b}, ${alpha})`;
		}
		if (color.startsWith('rgb')) {
			const nums = color.replace(/rgba?\(|\)|\s/g, '').split(',').map(n => parseFloat(n));
			const [r, g, b] = nums;
			return `rgba(${r}, ${g}, ${b}, ${alpha})`;
		}
		// Fallback: return color as-is (no alpha control)
		return color;
	} catch {
		return color as string;
	}
}

// Helper to normalize different date formats into MM/DD/YYYY for display
function formatDateToMMDDYYYY(value: string): string {
	if (!value) return value;

	// ISO format: YYYY-MM-DD
	const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (isoMatch) {
		const [, year, month, day] = isoMatch;
		return `${month}/${day}/${year}`;
	}

	// DD/MM/YYYY (previous behaviour) -> convert to MM/DD/YYYY
	const dmyMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
	if (dmyMatch) {
		const [, day, month, year] = dmyMatch;
		return `${month}/${day}/${year}`;
	}

	return value;
}

/** Fit short text (e.g. dates) in the PDF overlay without awkward line breaks. */
function fontSizePxForOverlay(text: string, boxWidthPx: number, opts?: { min?: number; max?: number; charFactor?: number }) {
	const min = opts?.min ?? 5.5;
	const max = opts?.max ?? 12;
	const charFactor = opts?.charFactor ?? 0.52;
	const len = Math.max(text.length, 1);
	const paddingAllowance = 6;
	const raw = (boxWidthPx - paddingAllowance) / (len * charFactor);
	const rounded = Math.round(raw * 10) / 10;
	return Math.max(min, Math.min(max, rounded));
}

// Helper to display phone as XXX-XXX-XXXX in the PDF (accepts digits only or already formatted)
function formatPhoneForDisplay(value: string): string {
	if (!value) return value;
	const digits = value.replace(/\D/g, "");
	if (digits.length !== 10) return value;
	return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export const fieldTypes = [
	{
		label: "Short Text",
		value: "shortText",
		icon: <TextIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.text,
	},
	{
		label: "Date",
		value: "date",
		icon: <DateRangeIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.date,
	},
	{
		label: "Number",
		value: "number",
		icon: <DialpadIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.text,
	},
	{
		label: "Select",
		value: "select",
		icon: <DehazeIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.select,
	},
	{
		label: "Checkbox",
		value: "checkbox",
		icon: <CheckBoxIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.checkbox,
	},
	{
		label: "Radio",
		value: "radio",
		icon: <RadioButtonCheckedIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.radio,
	},
	{
		label: "Signature",
		value: "signature",
		icon: <BorderColorIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.signature,
	},
	{
		label: "Initials",
		value: "initials",
		icon: <EditIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.initials,
	},
];

interface FieldValue {
	id: string;
	value: string;
	fieldType: string;
}

export function createFieldsPlugin(
	fields: FieldModel[],
	onSelect: (id: string) => void,
	selectedId: string | null,
	fieldValues: FieldValue[] = [],
	activeFieldId: string | null = null
) {
	return {
		renderPageLayer(props: any) {
			const { pageIndex, width: pageWidth, height: pageHeight } = props;
			// Calculate pixels per inch based on the actual page width (8.5 inches)
			const ppi = pageWidth / 8.5; // px por pulgada (escala actual)

			const pageFields = fields.filter((f) => f.fieldPosition?.page === pageIndex + 1);
			return (
				<div
					style={{
						position: "relative",
						width: pageWidth,
						height: pageHeight,
					}}
				>
					{pageFields.map((f) => {
						const { x, y, width, height } = f.fieldPosition || { x: 0, y: 0, width: 0, height: 0 };

						// Convert inches to pixels
						const left = x * ppi;
						const top = y * ppi;
						const w = width * ppi;
						const h = height * ppi;

						const resolvedTemplateType =
							(f as { type?: string; fieldType?: string }).type ||
							(f as { fieldType?: string }).fieldType;
						const iconField = fieldTypes.find((ft) => ft.value === resolvedTemplateType)?.icon;

						// Find the current value for this field
						const currentValue =
							fieldValues.find((v) => String(v.id) === String(f.id))?.value || "";
						const fieldType =
							fieldValues.find((v) => String(v.id) === String(f.id))?.fieldType ||
							resolvedTemplateType ||
							"shortText";

						// Determine field state
						const isSelected = String(f.id) === String(selectedId);
						const isActive = String(f.id) === String(activeFieldId);
						const hasValue = !!currentValue;

						// Dynamic styles based on state
						const getFieldStyles = () => {
							if (isActive) {
								return {
									outline: '3px solid #2196F3',
									boxShadow: '0 0 12px rgba(33, 150, 243, 0.6)',
									background: hasValue ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.2)',
									zIndex: 1002,
								};
							} else if (isSelected) {
								return {
									outline: '2px solid #ff9800',
									background: hasValue ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 152, 0, 0.5)',
									zIndex: 1001,
								};
							} else {
								return {
									outline: '1px solid #ff9800',
									background: hasValue ? 'rgba(255, 255, 255, 0.95)' : (f.fieldGroupColor ? withAlpha(f.fieldGroupColor as any, 0.3) : 'rgba(255, 152, 0, 0.3)'),
									zIndex: 1000,
								};
							}
						};

						const fieldStyles = getFieldStyles();

						// Render field value based on type
						const renderFieldValue = () => {
							if (!currentValue) return null;

							switch (fieldType) {
								case "checkbox":
									return currentValue === "true" ? (
										<div style={{ 
											width: "16px", 
											height: "16px", 
											backgroundColor: "#006ac6", 
											border: "2px solid #006ac6",
											borderRadius: "2px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center"
										}}>
											<span style={{ color: "white", fontSize: "12px" }}>✓</span>
										</div>
									) : null;
								case "signature":
									return (
										<img 
											src={currentValue} 
											alt="Signature" 
											style={{ 
												maxWidth: "100%", 
												maxHeight: "100%", 
												objectFit: "contain" 
											}} 
										/>
									);
								case "date": {
									const formatted = formatDateToMMDDYYYY(currentValue);
									const fontPx = fontSizePxForOverlay(formatted, w, {
										min: 5.5,
										max: Math.min(13, h * 0.85),
										charFactor: 0.5,
									});
									return (
										<div
											style={{
												width: "100%",
												height: "100%",
												boxSizing: "border-box",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												padding: "1px 3px",
												overflow: "hidden",
											}}
										>
											<span
												style={{
													fontSize: `${fontPx}px`,
													color: "#000",
													fontWeight: 600,
													textAlign: "center",
													lineHeight: 1.05,
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
													fontFamily:
														'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
													letterSpacing: "-0.02em",
												}}
											>
												{formatted}
											</span>
										</div>
									);
								}
								case "shortText":
								case "text":
								case "number":
								case "select":
								case "phone":
								case "initials":
								default: {
									const displayText =
										fieldType === "phone" || /^\d[\d-]*\d$|^\d{10}$/.test(currentValue)
											? formatPhoneForDisplay(currentValue)
											: currentValue;
									return (
										<div style={{
											fontSize: "10px",
											color: "#000",
											fontWeight: "500",
											textAlign: "center",
											wordBreak: "break-word",
											lineHeight: "1.2",
											padding: "2px"
										}}>
											{displayText}
										</div>
									);
								}
							}
						};

						return (
							<div
								key={f.id}
								data-field-id={f.id}
								style={{
									position: "absolute",
									left,
									top,
									width: w,
									height: h,
									boxSizing: "border-box",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									transition: 'all 0.2s ease-in-out',
									...fieldStyles,
								}}
							>
								{/* Field value overlay */}
								{currentValue && (
									<div
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											zIndex: 1001,
											pointerEvents: "none"
										}}
									>
										{renderFieldValue()}
									</div>
								)}
								
								{/* Field icon and title (only when no value) */}
								{!currentValue && (
									<div
										style={{
											width: "100%",
											height: "100%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: f.fieldGroupColor ? "#fff" : (String(f.id) === String(selectedId) ? "white" : "rgba(0, 106, 198, 1)"),
											background: f.fieldGroupColor ? withAlpha(f.fieldGroupColor as any, 0.6) : (String(f.id) === String(selectedId) ? "rgba(0, 106, 198, 0.8)" : "transparent"),
										}}
										onClick={(e) => {
											e.stopPropagation();
											onSelect(f.id);
										}}
										title={f.title}
									>
										<span style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: "8px" }}>
											{iconField} {f.title}
										</span>
									</div>
								)}
								
								{/* Clickable area for selection when there's a value */}
								{currentValue && (
									<div
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											cursor: "pointer",
											zIndex: 1002
										}}
										onClick={(e) => {
											e.stopPropagation();
											onSelect(f.id);
										}}
										title={`${f.title}: ${currentValue}`}
									/>
								)}
							</div>
						);
					})}
				</div>
			);
		},
	};
}
